/**
 * 「添加出版社 / 建一册」的**纯函数**：出版社校验、载荷、重复判定、候选清单。
 *
 * ## 一本书 = 一个具体的册（2026-10-18 定稿）
 *
 * 用户原话："我**不是说只留出版社**这一个要素，我是说**要去掉版次，其他都保留**。"
 * 随后又澄清（本轮）："你就**只建出版社**就可以了。至于年级和学期，它旁边不是有
 * 两个下拉菜单吗？"
 *
 * 所以：
 *   · 身份 = **出版社 + 年级 + 学期**（同一册就是同一本书）；
 *   · 界面上的浮窗**只有出版社名一格**，年级 / 学期由「按书录入」页面那两格给定
 *     （没选中课本时它们可选，默认 小学一年级 / 上册）；
 *   · 「版次」**从界面上去掉** —— 表单里没有这一格，请求体里也没有这个字段；
 *   · 下拉 / 列表里显示成「人民教育出版社 · 小学一年级 · 上册」，**不缀版次**。
 *
 * ⚠️ 这个模块里的 `NewTextbookInput` 仍然是**四个字段**：年级 / 学期从页面来，
 *    到这里合成那条 `POST /admin/dictation/textbooks` 的请求体。界面只有一格，
 *    不等于身份只有一格。
 *
 * ## 出版社：库里存**人话**（2026-10-18 修正）
 *
 * 改造前这里把用户写的 `人民教育出版社` 用 FNV-1a 算成 `pub_be07fb11` 再发出去，
 * 而「按书录入 → 保存时自动建书」那条路（`resolveTextbook`）写的是**人话**。
 * 于是同一家出版社在库里有两个值：唯一键 `(publisher, subject, grade_code, semester)`
 * 允许它们各自存在 ⇒ 筛选下拉出现两项、每一项只查到一半的字（生产实测：
 * 上册 280 字挂在 `pub_be07fb11` 下、下册 114 字挂在 `人民教育出版社` 下）。
 *
 * 现在**写库只有一个值**：人话。理由见 `@fht/shared` 的 `publisher.ts` 文件头。
 *
 * ⚠️ 归一只有一份实现：`canonicalPublisherName()`（`@fht/shared`），前端与服务层
 *    引的是同一个函数 —— 不在前端另写一套，否则两份口径迟早又漂移。
 * ⚠️ 显示也只有一个实现：`publisherDisplayNameOf()`。历史行（`publisher = pub_…`、
 *    人话在 `name` 列）显示成人话，界面上**永远不该出现 `pub_` 那串代号**。
 *
 * ## 后端真实的形状（照 `createTextbookSchema` + `textbook` 表抄，不猜）
 *
 * | 字段 | 必填 | 形状 |
 * | --- | --- | --- |
 * | `publisher` | **是** | 出版社名（人话，≤32）；历史代号 `pub_xxxxxxxx` 会被拒 |
 * | `gradeCode` | **是** | 年级 code（`primary_1` …） |
 * | `semester` | **是** | `上` / `下` / `不分` |
 * | `name` | **是** | 展示名（≤64）—— 留空时与「出版社」相同 |
 * | `subject` | 否 | 默认 `chinese`（目前只有语文） |
 * | `isDefault` / `sortOrder` | 否 | 本表单**不发**（默认 `false` / `0`） |
 */

import {
  PUBLISHER_NAME_MAX,
  canonicalPublisherName,
  gradeLabel,
  isGeneratedPublisherCode,
  normalizeTextbookText,
  publisherDisplayNameOf,
  publisherRefKeyOf,
  semesterTitle,
  textbookRefKey,
} from '@fht/shared';
import type { TextbookView } from './api/dictation';
import type { CreateTextbookPayload } from './api/dictationAdmin';

/** 表单里的字段（原样，用户写什么就是什么）。 */
export interface NewTextbookInput {
  /** 出版社：书封面 / 版权页上的那家（`人民教育出版社`），也可以是代号（`renjiao`）。 */
  publisher: string;
  /** 课本名称：展示用；留空 = 用「出版社」。 */
  name: string;
  /** 年级 code（`primary_1` …）—— **必填**（一本书 = 一册）。 */
  gradeCode: string;
  /** 学期（`上` / `下` / `不分`）—— **必填**。 */
  semester: string;
}

/** 字段长度上限（与 `textbook` 表的 varchar 和 zod 的 `.max()` 对齐）。 */
export const PUBLISHER_MAX = PUBLISHER_NAME_MAX;
export const TEXTBOOK_NAME_MAX = 64;

/** 展示名：留空就用「出版社」（与导入路径把课本列同时写进 publisher/name 是同一效果）。 */
export function textbookNameOf(input: Pick<NewTextbookInput, 'publisher' | 'name'>): string {
  const name = normalizeTextbookText(input.name);
  return name === '' ? normalizeTextbookText(input.publisher) : name;
}

/**
 * **出版社名**的本地校验（「添加出版社」浮窗只有这一格）。
 *
 * ⚠️ 年级 / 学期**不在这里**：它们由「按书录入」页面上的两格给定
 *    （没选中课本时可选，默认 小学一年级 / 上册），浮窗里不再重复问。
 *
 * ⚠️ 返回的一句话与后端 zod 的话同口径，因为界面上先说一遍
 *    总比让人看到一句"提交的内容有误"好。但它**不**用来禁用按钮 ——
 *    本项目反对"灰着不说话的按钮"。
 *
 * ⚠️ `pub_xxxxxxxx`（历史代号）在这里就被拦下：它不是一家出版社的名字，
 *    归一它只会得到空串 —— 与其让后端报一句技术话，不如在这里说清楚。
 */
export function validatePublisherName(rawPublisher: string): string | null {
  const raw = normalizeTextbookText(rawPublisher);
  if (raw === '') return '请填写出版社（书封面 / 版权页上的那家，例如 人民教育出版社）';

  if (isGeneratedPublisherCode(raw)) {
    return (
      '这不是出版社名：pub_ 开头的那串是旧数据留下的代号（库里看不出来是哪一家）。' +
      '请照书封面 / 版权页写名字，例如 人民教育出版社'
    );
  }

  const publisher = canonicalPublisherName(raw);
  if (publisher === '') return '请填写出版社（书封面 / 版权页上的那家，例如 人民教育出版社）';
  if (publisher.length > PUBLISHER_MAX) {
    return `出版社名太长了（最多 ${PUBLISHER_MAX} 个字，现在 ${publisher.length} 个）`;
  }

  return null;
}

/**
 * 本地校验（**点"建这本书"之前**把话说清楚）。
 *
 * ⚠️ 顺序按"用户最可能漏的那一格"排：出版社 → 年级 → 学期。年级 / 学期是
 *    **一本书 = 一册**的关键（上册下册是两本书），所以它们和出版社一样必填。
 *
 * ⚠️ 界面上的「添加出版社」浮窗**只调 `validatePublisherName`**（年级 / 学期
 *    在页面那两格，不在浮窗里）；这个函数留给同时持有三格值的调用方
 *    （例如将来别处要一次性建一册）。
 */
export function validateNewTextbookInput(input: NewTextbookInput): string | null {
  const publisherProblem = validatePublisherName(input.publisher);
  if (publisherProblem !== null) return publisherProblem;

  const gradeCode = input.gradeCode.trim();
  if (gradeCode === '') {
    return '请选年级：课本按「册」建 —— 一年级上册和一年级下册是两本书，要分别建。';
  }

  const semester = input.semester.trim();
  if (semester === '') {
    return '请选学期（上册 / 下册）：课本按「册」建 —— 一年级上册和一年级下册是两本书。';
  }

  const name = textbookNameOf(input);
  if (name.length > TEXTBOOK_NAME_MAX) {
    return `课本名称太长了（最多 ${TEXTBOOK_NAME_MAX} 个字，现在 ${name.length} 个）`;
  }

  return null;
}

/**
 * 建课本的请求体（`POST /admin/dictation/textbooks`）。
 *
 * ⚠️ `publisher` 发的是**归一后的人话**（`canonicalPublisherName`）——
 *    与 `resolveTextbook`（导入自动建书）**同一个值**，这是本次 bug 的修法：
 *    两条建书路径写库口径必须一致。
 * ⚠️ `gradeCode` / `semester` 原样发（它们是 code）。
 * ⚠️ `subject` / `isDefault` / `sortOrder` **不发**：后端 schema 有默认值
 *    （`chinese` / `false` / `0`），前端多发一份等于把默认值抄一遍，迟早漂移。
 * ⛔ **不发 `edition`**：版次已经从界面与接口里彻底去掉（2026-10-18）。
 */
export function buildCreateTextbookPayload(input: NewTextbookInput): CreateTextbookPayload {
  return {
    publisher: canonicalPublisherName(input.publisher),
    name: textbookNameOf(input),
    gradeCode: input.gradeCode.trim(),
    semester: input.semester.trim(),
  };
}

/**
 * 「这本书是不是已经有了」——**唯一键
 * `(publisher, subject, grade_code, semester)` 的界面版**。
 *
 * 判据（**年级 + 学期必须一致**，版次不参与）：
 *
 *   1. 已有那本的 `gradeCode` / `semester` 与我们要建的一致；
 *   2. 已有那本的**出版社身份**（`publisherRefKeyOf`：`publisher` 是人话就用它，
 *      是历史代号就退回 `name`）等于我们要发的人话；
 *   3. 或已有那本的 `publisher` / `name` 原样等于那串人话（兜底，老数据的两种形状）。
 *
 * 命中时**不要再发 POST**：这一册已经在了，直接把它选上。
 *
 * ⚠️ 第 2 条是 2026-10-18 补的：历史行 `publisher = pub_be07fb11` / `name = 人民教育出版社`
 *    以前只靠第 3 条（`name` 相等）命中 —— 现在两条都指向同一个键，不会再漏。
 */
export function findExistingTextbook(
  textbooks: readonly TextbookView[] | null | undefined,
  input: NewTextbookInput,
): TextbookView | null {
  const gradeCode = input.gradeCode.trim();
  const semester = input.semester.trim();
  if (gradeCode === '' || semester === '') return null;

  const canonical = canonicalPublisherName(input.publisher);
  const wantedKey = textbookRefKey(canonical) || textbookRefKey(input.publisher);
  if (wantedKey === '') return null;

  for (const textbook of textbooks ?? []) {
    if (textbook.gradeCode !== gradeCode || textbook.semester !== semester) continue;
    /**
     * 三种键任一相等就算同一家：出版社身份（人话）、`name`、`publisher` 原值。
     * 取"任一"而不是"全部"，因为历史数据的三个键本来就不齐。
     */
    const keys = [
      publisherRefKeyOf(textbook),
      textbookRefKey(textbook.name),
      textbookRefKey(textbook.publisher),
    ];
    if (keys.includes(wantedKey)) return textbook;
  }
  return null;
}

// ---------------------------------------------------------------- 候选清单（datalist）

/**
 * **已有出版社的候选**（`datalist`）—— 同一个出版社第二次录时是**选**，不是重打。
 *
 * 取的是**人话**（`publisherDisplayNameOf`）：历史行 `publisher = pub_…` / `name = 人话`
 * 给出人话；导入行两列都是人话。⛔ `pub_xxxxxxxx` 不会被当成候选给出去。
 *
 * ⛔ 版次已经没有候选了（2026-10-18）—— 那一格已从界面上删掉。
 */
export function listPublisherCandidates(
  textbooks: readonly TextbookView[] | null | undefined,
): string[] {
  const seen = new Set<string>();
  for (const textbook of textbooks ?? []) {
    const publisher = publisherDisplayNameOf(textbook);
    if (publisher !== '' && !isGeneratedPublisherCode(publisher)) seen.add(publisher);
    const name = normalizeTextbookText(textbook.name);
    if (name !== '' && !isGeneratedPublisherCode(name)) seen.add(name);
  }
  return [...seen].sort((a, b) => a.localeCompare(b, 'zh-CN'));
}

// ---------------------------------------------------------------- 文案

/** 一本书在提示语里的样子：`人民教育出版社 · 小学一年级 · 上册`。 */
export function describeNewTextbookIdentity(input: NewTextbookInput): string {
  return [
    normalizeTextbookText(input.publisher),
    gradeLabel(input.gradeCode),
    semesterTitle(input.semester),
  ]
    .filter((part) => part !== '')
    .join(' · ');
}

/** 建成功之后的那句话（含「出版社 · 年级 · 学期」，并说清下一步）。 */
export function describeNewTextbookCreated(input: NewTextbookInput): string {
  return `已添加出版社：${describeNewTextbookIdentity(input)} —— 已经帮你选上了，接着录三张表就行。`;
}

/**
 * **重复时**的那句话（用户能看懂，且不制造"我是不是做错了"的焦虑）。
 *
 * ⚠️ 刻意**不**报 409 那种技术话（后端那句是"数据冲突，请刷新后重试"）：
 *    用户要做的事只是"往下录字表"，而这件事现在就能做。
 *
 * ⚠️ `label` 传的是**已有那一本**在界面上的样子（`describeTextbookLabel()`：
 *    `人民教育出版社 · 小学一年级 · 上册`）—— 不是用户刚敲的那串。
 */
export function describeNewTextbookAlreadyExists(label: string): string {
  return `这一册的书已经有过了（${label}），已经帮你选上了 —— 接着录三张表就行。`;
}
